// src/lib/http.js
import axios from "axios";

/* ---------------- Helpers ---------------- */
const sleep = (ms) => new Promise((res) => setTimeout(res, ms));
const rand = (min, max) => Math.random() * (max - min) + min;
const now = () => (Date.now ? Date.now() : new Date().getTime());
const hasWin = typeof window !== "undefined";
const UA = hasWin ? navigator.userAgent : "";
const isSafari = hasWin && /Safari/.test(UA) && !/Chrome|CriOS|Edg/.test(UA);
const isIOS = hasWin && /iPhone|iPad|iPod/.test(UA);

/** crypto.randomUUID ზოგ iOS-ში არ არსებობს */
function genIdemKey() {
  try { if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID(); } catch (_) {}
  return Math.random().toString(36).slice(2) + String(now());
}

/** Exponential backoff + jitter */
function calcDelay(attempt, baseDelay, maxDelay) {
  const expo = baseDelay * Math.pow(2, attempt); // 0→base, 1→2x, 2→4x ...
  const jitter = rand(0.4, 1.0);
  return Math.min(Math.floor(expo * jitter), maxDelay);
}

/** Retry-After parser */
function parseRetryAfter(h) {
  if (!h) return null;
  const n = Number(h);
  if (!Number.isNaN(n)) return Math.max(0, Math.floor(n * 1000));
  const d = new Date(h);
  const delta = d.getTime() - now();
  return delta > 0 ? delta : null;
}

/** Normalize absolute URL to same-origin relative (www/apex შერევის თავიდან ასაცილებლად) */
function normalizeSameOrigin(url = "") {
  if (!hasWin) return url;
  try {
    if (!/^https?:\/\//i.test(url)) return url;
    const want = new URL(url);
    const cur  = new URL(window.location.href);
    // თუ სხვა ჰოსტია ან სხვა პროტოკოლია — გადაიყვანე relative-ში
    if (want.host !== cur.host || want.protocol !== cur.protocol) {
      return want.pathname + want.search + want.hash;
    }
    return want.pathname + want.search + want.hash;
  } catch { return url; }
}

/** Remove duplicate query keys (?v=0&v=0 → ?v=0) */
function dedupeQuery(url = "") {
  try {
    const base = hasWin ? window.location.origin : "https://shopaholics.ge";
    const u = new URL(url, base);
    const seen = new Set();
    const pairs = [];
    u.searchParams.forEach((val, key) => {
      if (seen.has(key)) return; // first wins
      seen.add(key);
      pairs.push([key, val]);
    });
    u.search = "";
    for (const [k, v] of pairs) u.searchParams.append(k, v);
    return u.pathname + (u.search || "") + (u.hash || "");
  } catch { return url; }
}

/* छोटे დებაუნს-ლოგერი, რომ კონსოლი არ გადაიღვაროს */
const errSeen = new Map();
function logHttpOnce(tag, cfgReq, error, extra = {}) {
  try {
    const m = (cfgReq?.method || "GET").toUpperCase();
    const u = cfgReq?.url || "";
    const s = error?.response?.status || 0;
    const key = `${tag}|${m}|${u}|${s}`;
    const last = errSeen.get(key) || 0;
    const t = now();
    if (t - last < 4000) return; // 4 წამში ერთხელ
    errSeen.set(key, t);
    const rid = error?.response?.headers?.["x-request-id"] || error?.response?.headers?.["X-Request-Id"] || "";
    const code = error?.code || "";
    const msg = error?.message || "";
    const attempt = cfgReq?.__retryCount || 0;
    // ერთი ხაზი — კმარა დიაგნოსტიკისთვის
    // eslint-disable-next-line no-console
    console.warn(`[HTTP:${tag}] ${s} ${m} ${u} ${rid ? "rid="+rid : ""} ${code ? "code="+code : ""} attempt=${attempt} ${extra.delay ? "retryIn="+extra.delay+"ms" : ""} ${extra.remaining ? "remain="+extra.remaining+"ms" : ""} msg=${msg}`);
  } catch {}
}

/* ---------------- Defaults ---------------- */
const DEFAULTS = {
  baseURL: "/",
  withCredentials: true,

  // per-attempt timeout
  timeout: 15_000,

  // retries per request (first try not counted)
  maxRetries: 3,

  // Whole request deadline including retries
  totalDeadlineMs: 30_000,

  retryMethods: ["get", "head", "options"],
  retryOnStatus: [500, 502, 503, 504, 507, 429, 508],

  baseDelay: 400,
  maxDelay: 6_000,

  addIdempotencyForWrites: true,

  // Mobile-safe parallelism
  maxConcurrent: (isIOS || isSafari) ? 4 : 8,
};

/* ---------------- Global concurrency guard ---------------- */
class Semaphore {
  constructor(max) { this.max = max; this.active = 0; this.queue = []; }
  async acquire() {
    if (this.active < this.max) { this.active++; return; }
    return new Promise((resolve) => this.queue.push(resolve));
  }
  release() {
    this.active = Math.max(0, this.active - 1);
    const next = this.queue.shift();
    if (next) { this.active++; next(); }
  }
}

/* ---------------- API factory ---------------- */
export function createApi(custom = {}) {
  const cfg = { ...DEFAULTS, ...custom };
  const api = axios.create({
    baseURL: cfg.baseURL,
    withCredentials: cfg.withCredentials,
    timeout: cfg.timeout,
  });

  const sem = new Semaphore(cfg.maxConcurrent);

  // Visibility-aware flag (ფონური polling-ები არ „გაქაჩონ“)
  let isHidden = false;
  if (hasWin && typeof document !== "undefined") {
    const handler = () => {
      isHidden = document.visibilityState === "hidden";
      // foreground-ზე დაბრუნებისას ოდნავ „შევარხიოთ“ რიგი (თუ რამე ჩარჩა)
      if (!isHidden) setTimeout(() => sem.release(), 0);
    };
    handler();
    document.addEventListener("visibilitychange", handler);
  }

  // --- Request interceptor (success)
  api.interceptors.request.use(async (req) => {
    req.__retryCount = req.__retryCount ?? 0;
    req.__startedAt = req.__startedAt ?? now();

    // normalize url (same-origin) + dedupe query
    if (req.url) {
      req.url = normalizeSameOrigin(req.url);
      req.url = dedupeQuery(req.url);
    }

    // concurrency guard
    await sem.acquire();
    req.__semAcquired = true;

    // Per-attempt AbortController
    if (typeof AbortController !== "undefined") {
      // თუ უკვე აქვს და aborted-ია — მოვაშოროთ
      if (req.signal && req.signal.aborted) req.signal = undefined;
      if (!req.signal) {
        const controller = new AbortController();
        req.signal = controller.signal;
        const t = setTimeout(() => controller.abort(), cfg.timeout);
        req.__abortTimeout = t;
        req.__abortController = controller;
      }
    }

    // idempotency on writes
    const method = (req.method || "get").toLowerCase();
    const isWrite = ["post", "put", "patch", "delete"].includes(method);
    if (isWrite && cfg.addIdempotencyForWrites && !req.headers?.["X-Idempotency-Key"]) {
      req.headers = req.headers || {};
      req.headers["X-Idempotency-Key"] = genIdemKey();
    }

    // throttle background polling
    if (isHidden && req?.meta?.polling) await sleep(300);

    return req;
  });

  // --- Request interceptor (error path) → არ დავკარგოთ სლოტი
  api.interceptors.request.use(undefined, (error) => {
    const cfgReq = error?.config || {};
    if (cfgReq.__abortTimeout) clearTimeout(cfgReq.__abortTimeout);
    if (cfgReq.__semAcquired) sem.release();
    return Promise.reject(error);
  });

  // --- Response interceptor (success)
  api.interceptors.response.use(
    (res) => {
      if (res.config?.__abortTimeout) clearTimeout(res.config.__abortTimeout);
      if (res.config?.__semAcquired) sem.release();
      return res;
    },

    // --- Response interceptor (error + retry logic)
    async (error) => {
      const cfgReq = error.config || {};
      if (cfgReq.__abortTimeout) clearTimeout(cfgReq.__abortTimeout);
      if (cfgReq.__semAcquired) sem.release();

      const method = (cfgReq.method || "get").toLowerCase();
      const status = error.response?.status;
      const isNetwork = !!error.isAxiosError && !error.response; // DNS/CORS/offline

      // Whole deadline check
      const totalElapsed = now() - (cfgReq.__startedAt || now());
      if (totalElapsed >= cfg.totalDeadlineMs) {
        logHttpOnce("giveup-deadline", cfgReq, error);
        return Promise.reject(error);
      }

      // Retry-After
      const retryAfterHdr =
        error.response?.headers?.["retry-after"] ||
        error.response?.headers?.["Retry-After"];
      const retryAfterMs = parseRetryAfter(retryAfterHdr);

      // retry conditions
      const canRetryMethod =
        cfg.retryMethods.includes(method) ||
        (cfg.addIdempotencyForWrites && ["post", "put", "patch", "delete"].includes(method));
      const shouldRetryStatus = status ? cfg.retryOnStatus.includes(status) : false;
      const shouldRetry = canRetryMethod && (isNetwork || shouldRetryStatus);

      if (!shouldRetry) {
        logHttpOnce("giveup-noretry", cfgReq, error);
        return Promise.reject(error);
      }

      // attempts cap
      const attempt = cfgReq.__retryCount || 0;
      if (attempt >= cfg.maxRetries) {
        logHttpOnce("giveup-maxretries", cfgReq, error);
        return Promise.reject(error);
      }

      // wait for network
      if (hasWin && typeof navigator !== "undefined" && navigator.onLine === false) {
        await new Promise((resolve) => {
          const onBack = () => { window.removeEventListener("online", onBack); resolve(); };
          window.addEventListener("online", onBack, { once: true });
        });
      }

      // compute delay (respect remaining time)
      const delay = retryAfterMs ?? calcDelay(attempt, cfg.baseDelay, cfg.maxDelay);
      const remaining = cfg.totalDeadlineMs - (now() - (cfgReq.__startedAt || now()));
      if (remaining <= 0) {
        logHttpOnce("giveup-remaining", cfgReq, error);
        return Promise.reject(error);
      }
      const waitMs = Math.min(delay, remaining);
      logHttpOnce("retry", cfgReq, error, { delay: waitMs, remaining });
      await sleep(waitMs);

      // next attempt
      cfgReq.__retryCount = attempt + 1;

      // purge old abort state (Safari-critical)
      if (cfgReq.__abortTimeout) { clearTimeout(cfgReq.__abortTimeout); cfgReq.__abortTimeout = undefined; }
      if (cfgReq.__abortController) { try { cfgReq.__abortController.abort(); } catch(_) {} cfgReq.__abortController = undefined; }
      if (cfgReq.signal && cfgReq.signal.aborted) cfgReq.signal = undefined;

      // reapply abort controller
      if (typeof AbortController !== "undefined") {
        const controller = new AbortController();
        cfgReq.signal = controller.signal;
        const t = setTimeout(() => controller.abort(), cfg.timeout);
        cfgReq.__abortTimeout = t;
        cfgReq.__abortController = controller;
      }

      // normalize url again and dedupe query (თავიდან params არ დაგვიგროვდეს)
      if (cfgReq.url) {
        cfgReq.url = normalizeSameOrigin(cfgReq.url);
        cfgReq.url = dedupeQuery(cfgReq.url);
      }

      return api(cfgReq);
    }
  );

  return api;
}

/* -------- Shared instance -------- */
let _api;
export const api = (() => {
  if (!_api) _api = createApi();
  return _api;
})();
